import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { filter, map } from 'rxjs';
import { LiveChatActions } from '../live-chat/live-chat.actions';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { MessagesActions } from '../messages/messages.actions';
import {
  AnalyticsError,
  AnalyticsService,
  Configurable,
  FormBeacon,
  InteractionEventGlobal,
  MiscEventGlobal,
  PendingPayloadsService,
} from '@rogers/analytics';
import { getBrand } from '../../../utils/getBrand';
import { selectAnalyticsState } from './analytics.reducer';
import { selectLanguage } from '../settings/settings.reducers';
import { VaSessionActions } from '../va-session/va-session.actions';
import { ChatAdminActions } from '../chat-admin/chat-admin.actions';
import { SafeSendActions } from '../safe-send/safe-send.actions';
import { SafeSendType } from '@ucx-js/chat-ui';
import { PostEscalationCurationActions } from '../post-escalation-curation/post-escalation-curation.actions';
import { OverlayActions } from '../overlay-state/overlay.actions';
import { AuthTokenActions } from '../auth-token/auth-token.actions';
import { EmailTranscriptActions } from '../email-transcript/email-transcript.actions';
import { MessageSubType } from '../../types/enum';
import { selectSafeSendState } from '../safe-send/safe-send.reducer';
import { RecaptchaActions } from '../recaptcha/recaptcha.actions';
import { UserInfoActions } from '../user-info/user-info.actions';
import { selectLiveChatState } from '../live-chat/live-chat.reducer';
import { AnalyticsAction } from './analytics.actions';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsEffects {
  adobeAnalyticsService: AnalyticsService;
  appName = 'unified-chat-js';

  constructor(
    private actions$: Actions,
    private store: Store,
    private pendingPayloadsService: PendingPayloadsService
  ) {
    const configuration = new Configurable();
    configuration.language = this.store.select(selectLanguage);
    this.adobeAnalyticsService = new AnalyticsService(
      configuration,
      getBrand().code,
      {},
      this.pendingPayloadsService,
      `${getBrand().applicationName}|${this.appName}`,
      null,
      null
    );
  }

  trackLiveChatEscalation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LiveChatActions.handleCreateChatSuccess),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.trackClick(
            new InteractionEventGlobal(this.appName, 'escalation')
          );
        })
      ),
    { dispatch: false }
  );

  trackResponse$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MessagesActions.dialogApiSuccess),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.trackClick(
            new InteractionEventGlobal(this.appName, 'response')
          );
        })
      ),
    { dispatch: false }
  );

  trackSessionExpired$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VaSessionActions.expireSession),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.trackClick(
            new InteractionEventGlobal(this.appName, 'session expired')
          );
        })
      ),
    { dispatch: false }
  );

  trackCustomizeJourneyOnStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatAdminActions.updateResponse),
        filter((action) => action.isFirstCall),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.trackMisc(
            new MiscEventGlobal(this.appName, 'start chat')
          );
        })
      ),
    { dispatch: false }
  );

  trackSkipSafeSend$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SafeSendActions.updateSafeSend),
        filter((action) => !action.value.submitRequested),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([action, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.trackClick(
            new InteractionEventGlobal(
              this.appName,
              action.value.type == SafeSendType.PROACTIVE
                ? 'decline proactive safesend'
                : 'decline agent safesend'
            )
          );
        })
      ),
    { dispatch: false }
  );

  trackCuration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PostEscalationCurationActions.updateSelectedItems),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([action, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          let url = '/overlay/curationSelection/';
          if (action.value.category) {
            url += action.value.category;
          }
          if (action.value.product) {
            url += '-' + action.value.product;
          }
          if (action.value.specificProduct) {
            url += '-' + action.value.specificProduct;
          }
          this.adobeAnalyticsService.trackPageLoad(url);
        })
      ),
    { dispatch: false }
  );

  trackCloseChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OverlayActions.closeApplication),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          const windowObj = window;
          if (windowObj['_analyticsHelpers']) {
            windowObj['_analyticsHelpers'].isTrackingCloseChat = true;
            this.adobeAnalyticsService.trackClick(
              new InteractionEventGlobal(this.appName, 'close')
            );
          }
        })
      ),
    { dispatch: false }
  );

  trackErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AuthTokenActions.handleSessionFailure,
          ChatAdminActions.handleChatAdminFailure,
          EmailTranscriptActions.setFailed,
          LiveChatActions.handleConnectionError,
          MessagesActions.onDialogApiFailed,
          RecaptchaActions.onError,
          SafeSendActions.onGetProactiveSsFormTemplateError,
          SafeSendActions.onSendSafeSendError,
          SafeSendActions.onGetQuestionnaire,
          UserInfoActions.onGetUserAccountError
        ),
        filter((action) => !!action.error),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([action, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.setErrors([
            <AnalyticsError>{
              category: 'virtual assistant',
              type: 'http',
              code: action.error.status?.toString(),
              API: action.error.url,
              field: '',
              message: action.error.message,
            },
          ]);
        })
      ),
    { dispatch: false }
  );

  trackStartConversation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatAdminActions.updateResponse),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        filter(
          ([action, vaState]) =>
            vaState.vaSessionId &&
            (action.isFirstCall || !action.beforeEscalation) &&
            action.value.isVa
        ),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.setMisc(
            new MiscEventGlobal(this.appName, 'conversation start')
          );
        })
      ),
    { dispatch: false }
  );

  trackSafesendFormDisplay$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(MessagesActions.addNewMessages),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([action, vaState]) => {
          if (action.messages[0].messageSubType == MessageSubType.SAFESEND) {
            const safesend =
              action.messages[0].messageComponent.componentData.safeSend;
            this.adobeAnalyticsService.setVirtualAssistant(vaState);
            this.adobeAnalyticsService.trackForm(
              new FormBeacon(
                safesend.type === SafeSendType.PROACTIVE
                  ? 'proactive safesend authentication'
                  : 'safesend authentication',
                '',
                this.appName,
                [], // [new Field('field name', 'field value')]
                {
                  formView: true, // user saw this form
                }
              )
            );
          }
        })
      ),
    { dispatch: false }
  );

  trackSafesendformSubmit$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SafeSendActions.sendSafeSendData),
        concatLatestFrom(() => [
          this.store.select(selectAnalyticsState),
          this.store.select(selectSafeSendState),
        ]),
        map(([, vaState, safesend]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.setForm(
            new FormBeacon(
              safesend.type === SafeSendType.PROACTIVE
                ? 'proactive safesend authentication'
                : 'safesend authentication',
              '',
              this.appName,
              [], // [new Field('field name', 'field value')]
              {
                formComplete: true,
              }
            )
          );
        })
      ),
    { dispatch: false }
  );

  trackCaptchaScore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RecaptchaActions.setRecaptcha),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([action, vaState]) => {
          if (action.isResolved) {
            this.adobeAnalyticsService.setVirtualAssistant(vaState);
            this.adobeAnalyticsService.setMisc(
              new MiscEventGlobal(
                this.appName,
                `isResolved:${action.isResolved} - isError:${action.isError}`
              )
            );
          }
        })
      ),
    { dispatch: false }
  );

  trackChatCreate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthTokenActions.startSessionSuccess),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.setMisc(
            new MiscEventGlobal(this.appName, 'chat created')
          );
        })
      ),
    { dispatch: false }
  );

  trackChatAbandoned$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AnalyticsAction.abandonedCurations,
          VaSessionActions.expireSession
        ),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.setMisc(
            new MiscEventGlobal(this.appName, 'abandoned chat')
          );
        })
      ),
    { dispatch: false }
  );

  trackAnswerChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LiveChatActions.receiveOperatorMessage),
        concatLatestFrom(() => [
          this.store.select(selectAnalyticsState),
          this.store.select(selectLiveChatState),
        ]),
        filter(
          ([, , liveChatState]) => liveChatState.receivedFirstOperatorMessage
        ),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.setMisc(
            new MiscEventGlobal(this.appName, 'chat answered')
          );
        })
      ),
    { dispatch: false }
  );

  trackUnAnsweredChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LiveChatActions.endLiveChat),
        concatLatestFrom(() => [
          this.store.select(selectAnalyticsState),
          this.store.select(selectLiveChatState),
        ]),
        filter(
          ([, , liveChatState]) => !liveChatState.receivedFirstOperatorMessage
        ),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.setMisc(
            new MiscEventGlobal(this.appName, 'unanswered chat')
          );
        })
      ),
    { dispatch: false }
  );

  trackTranscriptSubmitted$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EmailTranscriptActions.setSubmitted),
        concatLatestFrom(() => this.store.select(selectAnalyticsState)),
        map(([, vaState]) => {
          this.adobeAnalyticsService.setVirtualAssistant(vaState);
          this.adobeAnalyticsService.setMisc(
            new MiscEventGlobal(this.appName, 'transcript request submitted')
          );
        })
      ),
    { dispatch: false }
  );
}
